/** Date/time text shared by queue explanations and confirm-card values (#691, #1231). */

import { OFFICE_TZ } from './toronto-time'

export type WhenStyle = 'relative' | 'absolute' | 'date' | 'time'

export interface FormatWhenOptions {
  now?: number | string | Date
  style?: WhenStyle
}

export interface StructuredTimeBasis {
  text: string
  timestamp?: string | null
  relative?: 'auto' | 'elapsed' | 'countdown'
  end_timestamp?: string | null
  end_label?: string | null
  suffix?: string | null
}

const ISO_INSTANT = /\b\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?\b/g
const RELATIVE_LIMIT_MS = 7 * 24 * 60 * 60 * 1_000
const TORONTO_YEAR = new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone: OFFICE_TZ })

function tidy(value: string): string {
  return value.replaceAll('\u00a0', ' ').replaceAll('\u202f', ' ')
}

function asDate(value: number | string | Date): Date {
  return value instanceof Date ? new Date(value.getTime()) : new Date(value)
}

function absoluteWhen(value: Date, now: Date, style: Exclude<WhenStyle, 'relative'>): string {
  const includeYear = TORONTO_YEAR.format(value) !== TORONTO_YEAR.format(now)
  const options: Intl.DateTimeFormatOptions = { timeZone: OFFICE_TZ }

  if (style !== 'time') {
    options.month = 'short'
    options.day = 'numeric'
    if (includeYear) options.year = 'numeric'
  }
  if (style !== 'date') {
    options.hour = 'numeric'
    options.minute = '2-digit'
    options.hour12 = true
  }

  return tidy(new Intl.DateTimeFormat('en-US', options).format(value))
}

function relativeWhen(value: Date, now: Date): string {
  const delta = value.getTime() - now.getTime()
  const elapsed = Math.abs(delta)
  if (elapsed < 60_000) return 'now'

  let amount: string
  if (elapsed < 60 * 60_000) amount = `${Math.max(1, Math.round(elapsed / 60_000))}m`
  else if (elapsed < 24 * 60 * 60_000) amount = `${Math.max(1, Math.round(elapsed / (60 * 60_000)))}h`
  else amount = `${Math.max(1, Math.round(elapsed / (24 * 60 * 60_000)))}d`
  return delta > 0 ? `in ${amount}` : `${amount} ago`
}

/**
 * One people-facing timestamp format. Recent instants are compact and relative; older instants are absolute.
 * Date, time and year decisions are always made in America/Toronto rather than the browser's locale.
 */
export function formatWhen(iso: string, options: FormatWhenOptions = {}): string {
  const value = new Date(iso)
  if (Number.isNaN(value.valueOf())) return iso

  const now = asDate(options.now ?? Date.now())
  const stableNow = Number.isNaN(now.valueOf()) ? new Date(0) : now
  const style = options.style ?? 'relative'
  if (style === 'relative' && Math.abs(value.getTime() - stableNow.getTime()) < RELATIVE_LIMIT_MS) {
    return relativeWhen(value, stableNow)
  }
  return absoluteWhen(value, stableNow, style === 'relative' ? 'absolute' : style)
}

function dateTime(value: string, now: number, timeZone?: string): string | null {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.valueOf())) return null
  const current = new Date(now)
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  }
  if (parsed.getFullYear() !== current.getFullYear()) options.year = 'numeric'
  return tidy(new Intl.DateTimeFormat('en-US', options).format(parsed))
}

function compactDistance(value: string, now: number): { amount: string; future: boolean } | null {
  const parsed = Date.parse(value)
  if (Number.isNaN(parsed)) return null
  const delta = parsed - now
  const minutes = Math.abs(delta) / 60_000
  if (minutes < 1) return { amount: 'now', future: delta > 0 }
  if (minutes < 60) return { amount: `${Math.max(1, Math.round(minutes))} min`, future: delta > 0 }
  const hours = minutes / 60
  if (hours < 48) return { amount: `${Math.max(1, Math.round(hours))} h`, future: delta > 0 }
  const days = hours / 24
  if (days < 60) return { amount: `${Math.max(1, Math.round(days))} d`, future: delta > 0 }
  const months = days / 30
  if (months < 24) return { amount: `${Math.max(1, Math.round(months))} mo`, future: delta > 0 }
  return { amount: `${Math.max(1, Math.round(days / 365))} y`, future: delta > 0 }
}

function relativeText(value: string, mode: StructuredTimeBasis['relative'], now: number): string | null {
  const distance = compactDistance(value, now)
  if (!distance) return null
  if (distance.amount === 'now') return distance.amount
  if (mode === 'elapsed') return distance.amount
  if (mode === 'countdown') return distance.future ? `in ${distance.amount}` : `${distance.amount} ago`
  return distance.future ? `in ${distance.amount}` : `${distance.amount} ago`
}

/** Compact age such as "5 min ago"; empty when the timestamp does not parse. */
export function formatAge(value: string, now = Date.now()): string {
  const distance = compactDistance(value, now)
  if (!distance) return ''
  return distance.amount === 'now' ? 'now' : `${distance.amount} ago`
}

/** Local timestamp plus compact relative age, assembled from the API's structured lane reason. */
export function formatLaneBasis(basis: StructuredTimeBasis | string | null | undefined, now = Date.now(), timeZone?: string): string {
  if (!basis) return ''
  // A rolling deploy can briefly pair the new web with an old API response. Format those legacy strings too.
  if (typeof basis === 'string') return formatIsoTimestampsInText(basis, now, timeZone)

  let result = basis.text.trim()
  if (basis.timestamp) {
    const formatted = dateTime(basis.timestamp, now, timeZone)
    const relative = relativeText(basis.timestamp, basis.relative ?? 'auto', now)
    if (formatted) result += ` ${formatted}${relative ? ` (${relative})` : ''}`
  }
  if (basis.end_timestamp) {
    const formatted = dateTime(basis.end_timestamp, now, timeZone)
    if (formatted) result += ` (${basis.end_label?.trim() || 'ends'} ${formatted})`
  }
  if (basis.suffix?.trim()) result += ` ${basis.suffix.trim()}`
  return result
}

/** Replace serialized instants in display-only text; payloads and editable JSON remain byte-for-byte unchanged. */
export function formatIsoTimestampsInText(value: string, now = Date.now(), timeZone?: string): string {
  return value.replace(ISO_INSTANT, (match) => dateTime(match, now, timeZone) ?? match)
}
